import {
  EnumActivityLogType,
  EnumBroadcastResourcesType,
  EnumEntityReferenceType,
  Multilingual,
  Owner,
  PostHogEventName,
  useActivityLogCreateMutation,
  useActivityLogManyLazyQuery,
} from "gql/generated";
import { usePostHog } from "shared/hooks";
import { useAppContext } from "useAppContext";
import { useCallback, useRef } from "react";

export const useBroadcastPostHogEvents = (
  personId?: string,
  broadcastId?: string,
  personName?: string,
  broadcastOwner?: Owner,
  broadcastName?: Multilingual,
) => {
  const { dbUser } = useAppContext();
  const { trackPostHogException } = usePostHog();
  const { trackPostHogEvent } = usePostHog();
  const [createActivityLog] = useActivityLogCreateMutation();
  const [getActivityLogs] = useActivityLogManyLazyQuery();
  const resourceTrackingRef = useRef<
    {
      type: EnumActivityLogType;
      resourceId?: string;
    }[]
  >([]);

  const createNewActivityLog = useCallback(
    async (
      type: EnumActivityLogType,
      message: Multilingual,
      resourceId?: string,
    ) => {
      if (
        !broadcastId ||
        !dbUser?._id ||
        !personId ||
        resourceTrackingRef.current.some(
          item => item.type === type && item.resourceId === resourceId,
        )
      )
        return;

      // Track the resourceId to avoid duplicate activity logs
      resourceTrackingRef.current.push({ type, resourceId });

      // Check if there is an existing activity log for the broadcast and the type
      const { data: activityLogs } = await getActivityLogs({
        variables: {
          filter: {
            entity: {
              _entityId: broadcastId,
              type: EnumEntityReferenceType.Broadcast,
            },
            ...(resourceId ? { resourceId } : {}),
            initiatedByUser: dbUser?._id,
            initiatedByPerson: personId,
            type,
          },
          limit: 10,
        },
      });
      const isExistingActivityLog = activityLogs?.activityLogMany.some(
        log => log.type === type,
      );

      // Only create a new activity log if there is no existing activity log for
      // the broadcast and the type. This prevents duplicate activity logs from
      // being created.
      if (
        broadcastName &&
        broadcastOwner &&
        personName &&
        !isExistingActivityLog
      ) {
        try {
          await createActivityLog({
            variables: {
              record: {
                entity: {
                  _entityId: broadcastId,
                  type: EnumEntityReferenceType.Broadcast,
                },
                ...(resourceId ? { resourceId } : {}),
                entityName: broadcastName,
                initiatedBy: personName,
                owner: broadcastOwner,
                message,
                type,
                initiatedByPerson: personId,
                initiatedByUser: dbUser?._id,
              },
            },
          });
        } catch (error) {
          trackPostHogException(error, {
            fileName: "useBroadcastPostHogEvents.ts",
            method: "createNewActivityLog",
            message: "Error creating new activity log",
          });
        }
      }
    },
    [
      broadcastId,
      dbUser?._id,
      personId,
      personName,
      broadcastName,
      broadcastOwner,
      trackPostHogException,
      createActivityLog,
      getActivityLogs,
    ],
  );

  const trackBroadcastViewedEvent = useCallback(async () => {
    trackPostHogEvent(PostHogEventName.BroadcastViewed, {
      broadcastId,
      userId: dbUser?._id,
      personId,
    });

    if (broadcastName) {
      await createNewActivityLog(EnumActivityLogType.BroadcastViewed, {
        en: `${personName} viewed the broadcast named ${broadcastName.en}`,
        fr: `${personName} a vu le broadcast nommé ${broadcastName.fr || broadcastName.en}`,
        es: `${personName} ha visto el broadcast nombrado ${broadcastName.es || broadcastName.en}`,
      });
    }
  }, [
    broadcastId,
    dbUser?._id,
    personId,
    personName,
    broadcastName,
    createNewActivityLog,
    trackPostHogEvent,
  ]);

  const trackResourceViewedEvent = useCallback(
    async (
      resourceId: string,
      resourceName: Multilingual,
      resourceType: EnumBroadcastResourcesType,
    ) => {
      trackPostHogEvent(PostHogEventName.ResourceViewed, {
        resourceId,
        broadcastId,
        userId: dbUser?._id,
        personId,
      });

      if (broadcastName) {
        const hasTitle = !!resourceName && !!resourceName.en;
        const message = hasTitle
          ? {
              en: `${personName} viewed the resource ${resourceName.en} from the broadcast named ${broadcastName.en}`,
              es: `${personName} ha visto la ressource ${resourceName.es || resourceName.en} del broadcast nombrado ${broadcastName.es || broadcastName.en}`,
              fr: `${personName} a vu la ressource ${resourceName.fr || resourceName.en} du broadcast nommé ${broadcastName.fr || broadcastName.en}`,
            }
          : {
              en: `${personName} viewed the resource of type ${resourceType.toLowerCase().replace("_", " ")} from the broadcast named ${broadcastName.en}`,
              es: `${personName} ha visto la ressource de tipo ${resourceType.toLowerCase().replace("_", " ")} del broadcast nombrado ${broadcastName.es || broadcastName.en}`,
              fr: `${personName} a vu la ressource de type ${resourceType.toLowerCase().replace("_", " ")} du broadcast nommé ${broadcastName.fr || broadcastName.en}`,
            };
        await createNewActivityLog(
          EnumActivityLogType.ResourceViewed,
          message,
          resourceId,
        );
      }
    },
    [
      broadcastId,
      dbUser?._id,
      personId,
      personName,
      broadcastName,
      createNewActivityLog,
      trackPostHogEvent,
    ],
  );

  const trackResourceDownloadedEvent = useCallback(
    async (resourceId: string, resourceType: EnumBroadcastResourcesType) => {
      trackPostHogEvent(PostHogEventName.ResourceDownloaded, {
        resourceId,
        broadcastId,
        userId: dbUser?._id,
        personId,
      });

      // Limit the activity log to only PDF and Photos resources
      // If more resource types are added, this will need to be updated to
      // ensure an accurate message is created per resource type.
      if (
        broadcastName &&
        [
          EnumBroadcastResourcesType.Pdf,
          EnumBroadcastResourcesType.Photos,
        ].includes(resourceType)
      ) {
        await createNewActivityLog(
          resourceType === EnumBroadcastResourcesType.Pdf
            ? EnumActivityLogType.FileDownloaded
            : EnumActivityLogType.PhotosDownloaded,
          {
            en: `${personName} downloaded ${resourceType === EnumBroadcastResourcesType.Pdf ? "a file" : "the photos"} from the broadcast named ${broadcastName.en}`,
            fr: `${personName} a téléchargé ${resourceType === EnumBroadcastResourcesType.Pdf ? "un fichier" : "les photos"} du broadcast nommé ${broadcastName.fr || broadcastName.en}`,
            es: `${personName} ha descargado ${resourceType === EnumBroadcastResourcesType.Pdf ? "un archivo" : "las fotos"} del broadcast nombrado ${broadcastName.es || broadcastName.en}`,
          },
          resourceId,
        );
      } else {
        trackPostHogException(
          `Resource downloaded activityLog event called with invalid resource type: ${resourceType} or has no broadcast name. Take a look at the code to ensure that the correct resource type is triggering a trackResourceDownloadedEvent.`,
          {
            fileName: "useBroadcastPostHogEvents.ts",
            method: "trackResourceDownloadedEvent",
            message:
              "Resource downloaded activityLog event called with invalid resource type or has no broadcast name",
            extra: {
              resourceId,
              resourceType,
              broadcastId: broadcastId ?? "",
              broadcastName:
                broadcastName?.en ??
                broadcastName?.fr ??
                broadcastName?.es ??
                "",
            },
          },
        );
      }
    },
    [
      broadcastId,
      dbUser?._id,
      personId,
      personName,
      broadcastName,
      createNewActivityLog,
      trackPostHogEvent,
      trackPostHogException,
    ],
  );

  const trackResourcePlayedEvent = useCallback(
    async (resourceId: string) => {
      trackPostHogEvent(PostHogEventName.ResourcePlayed, {
        resourceId,
        broadcastId,
        userId: dbUser?._id,
        personId,
      });

      if (broadcastName) {
        await createNewActivityLog(
          EnumActivityLogType.VideoPlayed,
          {
            en: `${personName} played a video from the broadcast named ${broadcastName.en}`,
            fr: `${personName} a joué une vidéo du broadcast nommé ${broadcastName.fr || broadcastName.en}`,
            es: `${personName} ha reproducido un video del broadcast nombrado ${broadcastName.es || broadcastName.en}`,
          },
          resourceId,
        );
      }
    },
    [
      broadcastId,
      dbUser?._id,
      personId,
      personName,
      broadcastName,
      createNewActivityLog,
      trackPostHogEvent,
    ],
  );

  const trackResourceInProgressEvent = useCallback(
    (resourceId: string) => {
      trackPostHogEvent(PostHogEventName.ResourceInProgress, {
        resourceId,
        broadcastId,
        userId: dbUser?._id,
        personId,
      });
      // Note: The activity log for this is created in the backend
    },
    [broadcastId, dbUser?._id, personId, trackPostHogEvent],
  );

  const trackResourceCompletedEvent = useCallback(
    (resourceId: string) => {
      trackPostHogEvent(PostHogEventName.ResourceCompleted, {
        resourceId,
        broadcastId,
        userId: dbUser?._id,
        personId,
      });
      // Note: The activity log for this is created in the backend
    },
    [broadcastId, dbUser?._id, personId, trackPostHogEvent],
  );

  return {
    trackBroadcastViewedEvent,
    trackResourceViewedEvent,
    trackResourceDownloadedEvent,
    trackResourcePlayedEvent,
    trackResourceInProgressEvent,
    trackResourceCompletedEvent,
  };
};
